import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { axisTicks, columnLabel, formatNumber, niceScale, seriesColor } from '@/usage/format';
import type { Metric, UsageChartData } from '@/usage/series';

import styles from './UsageChart.module.css';

export interface UsageChartProps {
  data: UsageChartData;
  metric: Metric;
  /** Holds the previous render dimmed while a new range loads. */
  stale?: boolean;
  /** Take the height of the parent instead of a fixed one; the parent must size it. */
  fill?: boolean;
}

const HEIGHT = 300;
const NARROW_HEIGHT = 160;
const MIN_HEIGHT = 110;
const MIN_TICK_GAP = 56;
const MARGIN = { top: 12, right: 4, bottom: 30, left: 44 };
const RADIUS = 4;
const STACK_GAP = 2;

/** A path for a bar whose top corners are rounded and whose base sits flat. */
function roundedTop(x: number, y: number, width: number, height: number): string {
  const r = Math.min(RADIUS, width / 2, height);
  return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${
    x + width
  },${y + r}V${y + height}Z`;
}

/** Usage over time as bars stacked by API key, one bar per hour or per day. */
export function UsageChart({ data, metric, stale = false, fill = false }: UsageChartProps) {
  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [boxHeight, setBoxHeight] = useState(0);
  const [active, setActive] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) {
      return;
    }
    setWidth(element.clientWidth);
    setBoxHeight(element.clientHeight);
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
      setBoxHeight(entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { columns, end, granularity, series } = data;
  const totals = useMemo(
    () => columns.map((_, column) => series.reduce((sum, entry) => sum + entry[metric][column], 0)),
    [columns, series, metric],
  );
  const scale = niceScale(Math.max(0, ...totals));
  const allTicks = useMemo(() => axisTicks(columns, granularity), [columns, granularity]);

  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const height =
    fill && boxHeight > 0 ? Math.max(MIN_HEIGHT, boxHeight) : width < 560 ? NARROW_HEIGHT : HEIGHT;
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const band = plotWidth / columns.length;
  const barGap = Math.min(2, band * 0.3);
  const barWidth = Math.max(1, band - barGap);
  const y = (value: number) => MARGIN.top + plotHeight - (value / scale.max) * plotHeight;
  const empty = totals.every((total) => total === 0);
  const spacing = allTicks.length > 1 ? (allTicks[1].index - allTicks[0].index) * band : Infinity;
  const every = Math.max(1, Math.ceil(MIN_TICK_GAP / spacing));
  const ticks = allTicks.filter((_, index) => index % every === 0);

  function columnAt(clientX: number) {
    const bounds = frame.current!.getBoundingClientRect();
    const index = Math.floor((clientX - bounds.left - MARGIN.left) / band);
    return Math.min(columns.length - 1, Math.max(0, index));
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, (current: number) => number> = {
      ArrowLeft: (current) => current - 1,
      ArrowRight: (current) => current + 1,
      Home: () => 0,
      End: () => columns.length - 1,
    };
    const move = moves[event.key];
    if (!move) {
      return;
    }
    event.preventDefault();
    const next = move(active ?? columns.length - 1);
    setActive(Math.min(columns.length - 1, Math.max(0, next)));
  }

  const activeEnd = active === null ? null : (columns[active + 1] ?? end);
  const readout =
    active === null
      ? null
      : {
          label: columnLabel(columns[active], activeEnd!, granularity),
          rows: series.map((entry) => ({ entry, value: entry[metric][active] })),
          total: totals[active],
        };
  const columnX = active === null ? 0 : MARGIN.left + band * active;
  const tooltipSide =
    columnX < width / 2 ? { left: columnX + band + 10 } : { right: width - columnX + 10 };

  return (
    <div
      ref={frame}
      className={[styles.frame, stale ? styles.stale : '', fill ? styles.fill : '']
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-roledescription="chart"
      aria-label={`${metric === 'credits' ? 'Credits' : 'Requests'} per ${granularity}, stacked by API key. Use the arrow keys to read each ${granularity}.`}
      tabIndex={0}
      onPointerMove={(event) => setActive(columnAt(event.clientX))}
      onPointerLeave={() => setActive(null)}
      onFocus={() => setActive((current) => current ?? columns.length - 1)}
      onBlur={() => setActive(null)}
      onKeyDown={onKeyDown}
    >
      <svg width={width} height={height} aria-hidden="true" className={styles.svg}>
        {scale.ticks.map((tick) => (
          <g key={tick}>
            <line
              className={tick === 0 ? styles.baseline : styles.grid}
              x1={MARGIN.left}
              x2={width - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text className={styles.yLabel} x={MARGIN.left - 8} y={y(tick)} dy="0.32em">
              {formatNumber(tick)}
            </text>
          </g>
        ))}

        {active !== null ? (
          <rect
            className={styles.hover}
            x={MARGIN.left + band * active}
            y={MARGIN.top}
            width={band}
            height={plotHeight}
          />
        ) : null}

        {columns.map((_, column) => {
          const stack = series
            .map((entry) => ({ entry, value: entry[metric][column] }))
            .filter((item) => item.value > 0);
          let base = 0;
          return (
            <g key={column}>
              {stack.map(({ entry, value }, index) => {
                const yTop = y(base + value);
                const yBottom = y(base) - (index > 0 ? STACK_GAP : 0);
                base += value;
                const height = Math.max(1, yBottom - yTop);
                const x = MARGIN.left + band * column + barGap / 2;
                const fill = seriesColor(entry);
                return index === stack.length - 1 ? (
                  <path
                    key={entry.id}
                    d={roundedTop(x, yBottom - height, barWidth, height)}
                    fill={fill}
                  />
                ) : (
                  <rect
                    key={entry.id}
                    x={x}
                    y={yBottom - height}
                    width={barWidth}
                    height={height}
                    fill={fill}
                  />
                );
              })}
            </g>
          );
        })}

        {ticks.map((tick) => (
          <text
            key={tick.index}
            className={styles.xLabel}
            x={MARGIN.left + band * tick.index + (granularity === 'day' ? band / 2 : 0)}
            y={height - 8}
          >
            {tick.label}
          </text>
        ))}
      </svg>

      {empty ? (
        <p className={styles.empty}>
          No {metric} in this period. Calls made with your API keys show up here within a minute.
        </p>
      ) : null}

      {readout ? (
        <div className={styles.tooltip} style={tooltipSide} aria-hidden="true">
          <div className={styles.tooltipTitle}>{readout.label}</div>
          <ul>
            {readout.rows.map(({ entry, value }) => (
              <li key={entry.id}>
                <span className={styles.key} style={{ background: seriesColor(entry) }} />
                <strong>{formatNumber(value)}</strong>
                <span className={styles.tooltipLabel}>{entry.label}</span>
              </li>
            ))}
          </ul>
          {readout.rows.length > 1 ? (
            <div className={styles.tooltipTotal}>
              <strong>{formatNumber(readout.total)}</strong> {metric} in total
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.visuallyHidden} aria-live="polite">
        {readout
          ? `${readout.label}: ${readout.rows
              .map(({ entry, value }) => `${entry.label} ${formatNumber(value)}`)
              .join(', ')}; ${formatNumber(readout.total)} ${metric} in total.`
          : ''}
      </div>
    </div>
  );
}
